import { useEffect } from "react";

type JsonLd = Record<string, unknown> | Record<string, unknown>[];

interface AlternateLink {
  /** BCP-47 hreflang value, e.g. "en", "es", "x-default". */
  hreflang: string;
  /** Path-only, e.g. "/es/rsvp-info". */
  path: string;
}

interface SEOProps {
  title: string;
  description: string;
  /** Path-only canonical, e.g. "/rpe-info". Defaults to current pathname. */
  canonicalPath?: string;
  /** Absolute URL to an OG image. Defaults to the site-wide image. */
  ogImage?: string;
  /** Optional JSON-LD structured data. Pass an object or array of objects. */
  jsonLd?: JsonLd;
  /** BCP-47 locale of this page; sets <html lang> and og:locale. */
  locale?: string;
  /** Alternate-language URLs for hreflang tags. */
  alternates?: AlternateLink[];
}

const SITE_ORIGIN = "https://vote.gameon.futbol";
const DEFAULT_OG_IMAGE =
  "https://etrspsphgbkapczqxboy.supabase.co/storage/v1/object/public/vote-images/VoteAppImages/og-image.jpg";

/** Find or create a <meta> tag and set its content. Returns the previous value (or null). */
function setMeta(
  selector: string,
  attr: "name" | "property",
  key: string,
  content: string,
): string | null {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  const created = !el;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  const prev = created ? null : el.getAttribute("content");
  el.setAttribute("content", content);
  return prev;
}

/**
 * Per-page SEO + AI-agent metadata. Sets <title>, description, canonical link,
 * Open Graph + Twitter tags, and optional JSON-LD structured data. Restores
 * the previous values on unmount so navigation between pages is clean.
 */
export function SEO({
  title,
  description,
  canonicalPath,
  ogImage = DEFAULT_OG_IMAGE,
  jsonLd,
  locale,
  alternates,
}: SEOProps) {
  useEffect(() => {
    const path =
      canonicalPath ?? window.location.pathname + window.location.search;
    const canonicalUrl = `${SITE_ORIGIN}${path.startsWith("/") ? path : `/${path}`}`;

    // Title
    const prevTitle = document.title;
    document.title = title;

    // Description + OG/Twitter meta
    const prevDesc = setMeta(
      'meta[name="description"]',
      "name",
      "description",
      description,
    );
    const prevOgTitle = setMeta(
      'meta[property="og:title"]',
      "property",
      "og:title",
      title,
    );
    const prevOgDesc = setMeta(
      'meta[property="og:description"]',
      "property",
      "og:description",
      description,
    );
    const prevOgUrl = setMeta(
      'meta[property="og:url"]',
      "property",
      "og:url",
      canonicalUrl,
    );
    const prevOgImage = setMeta(
      'meta[property="og:image"]',
      "property",
      "og:image",
      ogImage,
    );
    const prevTwTitle = setMeta(
      'meta[name="twitter:title"]',
      "name",
      "twitter:title",
      title,
    );
    const prevTwDesc = setMeta(
      'meta[name="twitter:description"]',
      "name",
      "twitter:description",
      description,
    );
    const prevTwImage = setMeta(
      'meta[name="twitter:image"]',
      "name",
      "twitter:image",
      ogImage,
    );

    // Canonical
    let canonicalEl = document.head.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]',
    );
    const canonicalCreated = !canonicalEl;
    if (!canonicalEl) {
      canonicalEl = document.createElement("link");
      canonicalEl.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalEl);
    }
    const prevCanonical = canonicalCreated
      ? null
      : canonicalEl.getAttribute("href");
    canonicalEl.setAttribute("href", canonicalUrl);

    // <html lang> + og:locale
    const prevHtmlLang = document.documentElement.getAttribute("lang");
    if (locale) {
      document.documentElement.setAttribute("lang", locale);
    }
    const ogLocaleMap: Record<string, string> = {
      en: "en_US",
      es: "es_419",
      "es-419": "es_419",
      "pt-BR": "pt_BR",
      fr: "fr_FR",
    };
    const ogLocaleValue = locale ? (ogLocaleMap[locale] ?? locale.replace("-", "_")) : undefined;
    const prevOgLocale = ogLocaleValue
      ? setMeta(
          'meta[property="og:locale"]',
          "property",
          "og:locale",
          ogLocaleValue,
        )
      : null;

    // hreflang alternates — remove any prior page-scoped ones first
    const prevAlternates = Array.from(
      document.head.querySelectorAll<HTMLLinkElement>(
        'link[rel="alternate"][data-seo="alt"]',
      ),
    );
    prevAlternates.forEach((el) => el.remove());
    const createdAlternates: HTMLLinkElement[] = [];
    if (alternates && alternates.length > 0) {
      alternates.forEach(({ hreflang, path: altPath }) => {
        const link = document.createElement("link");
        link.setAttribute("rel", "alternate");
        link.setAttribute("hreflang", hreflang);
        link.setAttribute(
          "href",
          `${SITE_ORIGIN}${altPath.startsWith("/") ? altPath : `/${altPath}`}`,
        );
        link.dataset.seo = "alt";
        document.head.appendChild(link);
        createdAlternates.push(link);
      });
    }

    // JSON-LD
    const ldScript = document.createElement("script");
    if (jsonLd) {
      ldScript.type = "application/ld+json";
      ldScript.dataset.seo = "page";
      ldScript.text = JSON.stringify(jsonLd);
      document.head.appendChild(ldScript);
    }

    return () => {
      document.title = prevTitle;
      if (prevDesc !== null)
        setMeta('meta[name="description"]', "name", "description", prevDesc);
      if (prevOgTitle !== null)
        setMeta('meta[property="og:title"]', "property", "og:title", prevOgTitle);
      if (prevOgDesc !== null)
        setMeta(
          'meta[property="og:description"]',
          "property",
          "og:description",
          prevOgDesc,
        );
      if (prevOgUrl !== null)
        setMeta('meta[property="og:url"]', "property", "og:url", prevOgUrl);
      if (prevOgImage !== null)
        setMeta('meta[property="og:image"]', "property", "og:image", prevOgImage);
      if (prevTwTitle !== null)
        setMeta('meta[name="twitter:title"]', "name", "twitter:title", prevTwTitle);
      if (prevTwDesc !== null)
        setMeta(
          'meta[name="twitter:description"]',
          "name",
          "twitter:description",
          prevTwDesc,
        );
      if (prevTwImage !== null)
        setMeta(
          'meta[name="twitter:image"]',
          "name",
          "twitter:image",
          prevTwImage,
        );

      if (canonicalCreated) {
        canonicalEl?.remove();
      } else if (prevCanonical !== null) {
        canonicalEl?.setAttribute("href", prevCanonical);
      }

      if (locale) {
        if (prevHtmlLang !== null) {
          document.documentElement.setAttribute("lang", prevHtmlLang);
        } else {
          document.documentElement.removeAttribute("lang");
        }
      }
      if (prevOgLocale !== null) {
        setMeta('meta[property="og:locale"]', "property", "og:locale", prevOgLocale);
      }

      createdAlternates.forEach((el) => el.remove());

      if (jsonLd && ldScript.parentNode) {
        ldScript.parentNode.removeChild(ldScript);
      }
    };
  }, [title, description, canonicalPath, ogImage, jsonLd, locale, alternates]);

  return null;
}
